/* eslint-disable consistent-return */
import React, { useState, useEffect, useRef } from 'react';
import 'styled-components/macro';
import Button from 'components/atoms/Button';
import Toast from 'components/molecules/Toast';
import { AuthContext } from 'context/authContext';
import { MqttContext } from 'context/mqttContext';
import { useContextHook } from 'use-context-hook';

const TIMEOUT_MS = 60000; // 60 seconds timeout

// Helper to get friendly error message and hint
const getErrorInfo = errorMsg => {
  const lower = (errorMsg || '').toLowerCase();
  if (lower.includes('not found')) {
    return {
      title: 'No logs found for today',
      hint: 'Logs may have already been uploaded, or no activity has occurred yet. Try doing a transaction on the device first.',
    };
  }
  if (lower.includes('timeout') || lower.includes('offline') || lower.includes('unresponsive')) {
    return {
      title: 'Device not responding',
      hint: 'Make sure the device is online and connected to the internet.',
    };
  }
  return {
    title: errorMsg || 'Something went wrong',
    hint: 'Please try again or contact support if the issue persists.',
  };
};

function DownloadDeviceLogs({ onClose, device }) {
  const [status, setStatus] = useState('sending'); // 'sending' | 'waiting' | 'success' | 'error'
  const [message, setMessage] = useState('Sending request...');
  const [errorInfo, setErrorInfo] = useState(null);
  const timeoutRef = useRef(null);
  const hasRequestedRef = useRef(false);

  const { user } = useContextHook(AuthContext, { user: 1 });
  const { connRef, resub, subscribe } = useContextHook(MqttContext, { connRef: 1, resub: 1, subscribe: 1 });

  // Generate today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle MQTT response - listen on user._id queue (callbackQueue)
  useEffect(() => {
    if (!user?._id) return () => {};

    const handleLogResponse = event => {
      const data = event.detail;
      // Check if this response is for our device and is a log file response
      if (data?.serial_number === device?.serial_number) {
        // Clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        if (data.success) {
          setStatus('success');
          setMessage(data.message || 'Logs uploaded successfully!');
          Toast({ type: 'success', message: 'Logs uploaded successfully' });
        } else {
          setStatus('error');
          setErrorInfo(getErrorInfo(data.error));
        }
      }
    };

    // Listen on user._id - that's the callbackQueue we send
    window.addEventListener(user._id, handleLogResponse);
    return () => {
      window.removeEventListener(user._id, handleLogResponse);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [device?.serial_number, user?._id]);

  const sendLogRequest = () => {
    if (hasRequestedRef.current) return;
    hasRequestedRef.current = true;

    try {
      if (!connRef.current || typeof connRef.current.isConnected !== 'function' || !connRef.current.isConnected()) {
        setStatus('error');
        setErrorInfo(getErrorInfo('MQTT not connected'));
        return;
      }

      const dateString = getTodayDate();

      const logRequest = {
        type: 'request_log_file',
        date: dateString,
        callbackQueue: user._id,
      };

      connRef.current.send(device.serial_number, JSON.stringify(logRequest), 2, false);

      setStatus('waiting');
      setMessage('Waiting for device...');

      // Set timeout
      timeoutRef.current = setTimeout(() => {
        setStatus('error');
        setErrorInfo(getErrorInfo('Device not responding - timeout'));
      }, TIMEOUT_MS);
    } catch (error) {
      setStatus('error');
      setErrorInfo(getErrorInfo(error.message));
    }
  };

  // Subscribe to user queue and send request on mount
  useEffect(() => {
    if (!connRef.current || hasRequestedRef.current || !user?._id) return;

    subscribe(user._id, {
      onSuccess: () => sendLogRequest(),
      onFailure: () => {
        setStatus('error');
        setErrorInfo(getErrorInfo('Failed to connect'));
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resub]);

  const handleRetry = () => {
    hasRequestedRef.current = false;
    setStatus('sending');
    setMessage('Sending request...');
    setErrorInfo(null);
    sendLogRequest();
  };

  const isLoading = status === 'sending' || status === 'waiting';

  return (
    <div css="padding: 16px;">
      <div css="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100px; text-align: center;">
        {isLoading && (
          <div css="display: flex; align-items: center; gap: 12px;">
            <div
              css={`
                width: 24px;
                height: 24px;
                border: 2px solid #e0e0e0;
                border-top-color: #1976d2;
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
                @keyframes spin {
                  to {
                    transform: rotate(360deg);
                  }
                }
              `}
            />
            <span css="font-size: 14px; color: #555;">{message}</span>
          </div>
        )}

        {status === 'success' && (
          <div css="display: flex; align-items: center; gap: 10px;">
            <i className="material-icons-outlined" css="font-size: 28px; color: #4caf50;">
              check_circle
            </i>
            <span css="font-size: 14px; color: #333;">{message}</span>
          </div>
        )}

        {status === 'error' && errorInfo && (
          <div css="width: 100%;">
            <div css="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">
              <i className="material-icons-outlined" css="font-size: 24px; color: #f44336;">
                error_outline
              </i>
              <span css="font-size: 14px; color: #333; font-weight: 500;">{errorInfo.title}</span>
            </div>
            <div css="font-size: 12px; color: #666; margin-left: 34px; margin-bottom: 12px;">{errorInfo.hint}</div>
            <div css="display: flex; justify-content: center;">
              <Button type="outline" sm onClick={handleRetry}>
                Retry
              </Button>
            </div>
          </div>
        )}
      </div>

      <div css="display: flex; justify-content: flex-end; margin-top: 16px; border-top: 1px solid #eee; padding-top: 12px;">
        <Button type="light" sm onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

export default DownloadDeviceLogs;
